/**
 * Shared helpers for reading the credit-collection series returned by
 * POST /getcreditcollectiontrend (node/routes/trends.js →
 * queries/misc.js qryGetCreditCollectionTrend).
 *
 * Two screens consume that series — the dashboard's Credit Collection Overview
 * card (DashGrid.jsx) and the Credit sheet's CollectionTargetBar — and they must
 * agree on which row is "this month", so the lookup lives here rather than being
 * written twice.
 *
 * WHY NOT INDEX FROM THE END
 * The route pads the series two months into the future
 * (`endDateObj.setMonth(endDateObj.getMonth() + 2)`), so the current month is
 * *usually* the third row from the end, and DashGrid used to read
 * `chartData[chartData.length - 3]` on that basis.
 *
 * But setMonth overflows whenever today's day-of-month exceeds the length of the
 * month two ahead, and then rolls into a third month — so the series gains a
 * FOURTH trailing row and that index points one month too early, reporting the
 * previous month's figures as this month's. The days this happens on are
 * 31 July (+2 = 31 September = 1 October) and 29-31 December (+2 = 31 February
 * = 1-3 March): seven days every two years, each one a silently wrong KPI on a
 * card nobody would think to distrust.
 *
 * Matching on the row's own 'YYYY-MM' key cannot drift, whatever the padding does.
 */

// The window (in months back) requested from /getcreditcollectiontrend.
// Shared so the dashboard's month selector and the Credit sheet's bar start on
// the same react-query key and the second screen visited reads from cache — the
// key is [.., DealershipID, months], so they only share an entry while they ask
// for the same window. The value does not affect any single month's figures;
// targets and collections are computed per month, independently of the window.
export const DEFAULT_COLLECTION_MONTHS = 6;

//The series keys months as 'YYYY-MM' (DATE_FORMAT(m.MonthStart, '%Y-%m')).
export function monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The row for `date`'s month, or undefined if the series does not cover it.
 * `data` may be undefined while the query is still in flight.
 */
export function findMonthRow(data, date) {
    if (!Array.isArray(data)) return undefined;
    const key = monthKey(date);
    return data.find((row) => row?.month === key);
}

export function currentMonthRow(data, now = new Date()) {
    return findMonthRow(data, now);
}

/**
 * The row for the month before `now`. Built with new Date(y, m - 1, 1) rather
 * than by decrementing the key, so December correctly steps back to November of
 * the previous year. Day 1 is deliberate: constructing from today's day-of-month
 * would overflow (31 March minus one month is 3 March, not February).
 */
export function previousMonthRow(data, now = new Date()) {
    return findMonthRow(data, new Date(now.getFullYear(), now.getMonth() - 1, 1));
}
